import React, { useRef, useEffect, useState } from "react"
import ReactDOM from "react-dom"
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  Chip,
  Breakpoint
} from "@mui/material"
import { Skeleton } from "@mui/material"
import { visuallyHidden } from "@mui/utils"

import { makeStyles } from "tss-react/mui"

import {
  TransformWrapper,
  TransformComponent,
  type ReactZoomPanPinchRef
} from "react-zoom-pan-pinch"
import { FaIcon } from "../../../../mp-core/icons"
import { defaultPrevented } from "../../../generic-shared-components/helpers/eventHandlers"
import { formatDataAutomationId } from "@masteryprep/qti-components"
import { blueGrey } from "../../../../mp-core/themes/palette"
import { makeMathAccessible } from "./helpers/makeMathAccessible"
// a11y: imported directly rather than read from theme.mixins — each image is mounted
// with ReactDOM.render into a detached root that has no ThemeProvider, so theme.mixins
// is MUI's default there and has no focusRing (see QtiTableContent for the same pattern)
import { focusRing } from "../../../../mp-core/themes/focusStyles"
import {
  MAX_SCALE,
  MIN_SCALE,
  type PanDirection,
  clampScale,
  getBoundedPosition,
  getCenteredZoomPosition,
  getNextScale,
  getPannedPosition,
  getTransformBounds
} from "./imageTransformHelpers"

interface Props {
  question: string | null
  fontValue?: number | string | undefined
  showQuestion?: (loading: boolean) => void
  disableInlineZoom?: boolean
  enableFocus?: boolean // When true, content is focusable (for main question text)
}

const ZOOM_PERCENTAGE_ID = "current-zoom-level"
const ZOOM_PERCENTAGE_ID_MODAL = "current-zoom-level-modal"

const getPanDirectionFromKey = (key: string): PanDirection | null => {
  switch (key) {
    case "ArrowUp":
      return "up"
    case "ArrowDown":
      return "down"
    case "ArrowLeft":
      return "left"
    case "ArrowRight":
      return "right"
    default:
      return null
  }
}

const getTransformState = (transformApi: ReactZoomPanPinchRef) =>
  transformApi.instance?.transformState ?? transformApi.state

const getCurrentBounds = (
  transformApi: ReactZoomPanPinchRef,
  scale: number
) => {
  const { bounds, contentComponent, setup, wrapperComponent } =
    transformApi.instance

  if (!wrapperComponent || !contentComponent) return bounds

  return getTransformBounds({
    wrapperWidth: wrapperComponent.offsetWidth,
    wrapperHeight: wrapperComponent.offsetHeight,
    contentWidth: contentComponent.offsetWidth,
    contentHeight: contentComponent.offsetHeight,
    scale,
    centerZoomedOut: Boolean(setup?.centerZoomedOut)
  })
}

const getCenteredPosition = (
  transformApi: ReactZoomPanPinchRef,
  scale: number
) => {
  const { wrapperComponent } = transformApi.instance
  const transformState = getTransformState(transformApi)

  if (!wrapperComponent || !transformState) {
    return {
      positionX: transformState?.positionX ?? 0,
      positionY: transformState?.positionY ?? 0
    }
  }

  return getCenteredZoomPosition(
    {
      scale: transformState.scale ?? MIN_SCALE,
      positionX: transformState.positionX ?? 0,
      positionY: transformState.positionY ?? 0
    },
    {
      width: wrapperComponent.offsetWidth,
      height: wrapperComponent.offsetHeight
    },
    scale
  )
}

const ImageMagnifier: React.FC<React.PropsWithChildren<Props>> = props => {
  const { question, disableInlineZoom = false, enableFocus = false } = props
  const questionRef = useRef<any>()

  useEffect(() => {
    replaceImagesWithMagnifierinDOM()
  }, [question])

  const replaceImagesWithMagnifierinDOM = () => {
    const magnifierElement = questionRef.current
    if (magnifierElement && question) {
      // Step 1: Set innerHTML first (as Celeste noted - required before querySelector!)
      magnifierElement.innerHTML = question

      // Step 2: Apply math accessibility to the DOM
      makeMathAccessible(magnifierElement)

      // Step 3: Handle highlighted sections
      magnifierElement
        .querySelector(".hl")
        ?.scrollIntoView({ behavior: "smooth", block: "center" })

      // Step 4: Replace images with magnifier components
      const images = [...magnifierElement.querySelectorAll("img")]
      if (images.length) {
        images.forEach((i: HTMLImageElement, index: number) => {
          const renderElement = document.createElement("div")
          if (renderElement) {
            wrapper(renderElement, i)
            images[index]?.replaceWith(renderElement)
          }
        })
      }
    }
  }

  const wrapper = (renderElement: HTMLDivElement, image: HTMLImageElement) => {
    const { src, classList } = image
    let { className } = image
    const styles = image.getAttribute("style")

    // to contain the sizing of images as default question images
    if (!classList.contains("img-fluid")) className += " img-fluid "
    ReactDOM.render(
      <MagnifierWrapper
        src={src}
        className={className}
        styles={styles}
        disableInlineZoom={disableInlineZoom}
      />,
      renderElement
    )
  }

  return (
    <Typography
      {...{ ref: questionRef }}
      component="div"
      id="container"
      variant="body2"
      tabIndex={enableFocus ? 0 : undefined}
    ></Typography>
  )
}

type MagnifierWrapperProps = {
  imageProps?: React.ImgHTMLAttributes<HTMLImageElement> & {
    "data-description"?: string
    "data-idx"?: string | number
  }
  isCropped?: boolean
  blobLoading?: boolean
  src?: string
  className?: string
  modalImageClassname?: string
  styles?: string
  disableInlineZoom?: boolean
  stopPropagation?: boolean
  onImageLoaded?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void
  isStrikethrough?: boolean
  tabIndex?: number
}

export const MagnifierWrapper = React.forwardRef<
  HTMLImageElement,
  MagnifierWrapperProps
>((props, externalImageRef) => {
  const {
    src,
    blobLoading,
    isCropped,
    imageProps,
    className,
    modalImageClassname,
    styles,
    disableInlineZoom = false,
    stopPropagation = false,
    onImageLoaded,
    isStrikethrough,
    tabIndex = 0
  } = props

  const [showButtons, setShowButtons] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [zoom, setZoom] = useState(MIN_SCALE)
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null)

  const imageRef = useRef<any>()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { classes, cx } = useStyles()

  const setImageRef = (node: HTMLImageElement | null) => {
    if (typeof externalImageRef === "function") {
      externalImageRef(node)
      return
    }

    if (externalImageRef) {
      const mutableExternalImageRef =
        externalImageRef as React.MutableRefObject<HTMLImageElement | null>
      mutableExternalImageRef.current = node
      return
    }

    imageRef.current = node
  }

  const formatStringToCamelCase = (str: any) => {
    const splitted = str?.split("-")
    if (splitted?.length === 1) return splitted[0]
    return (
      splitted[0] +
      splitted
        ?.slice(1)
        ?.map((word: any) => word[0]?.toUpperCase() + word?.slice(1))
        ?.join("")
    )
  }

  const getStyleObjectFromString = (str: any) => {
    const style = {}
    str?.split(";")?.forEach((el: any) => {
      const [property, value] = el?.split(":")
      if (!property) return
      const formattedProperty = formatStringToCamelCase(property?.trim())
      style[formattedProperty] = value?.trim()
    })
    const pe = { pointerEvents: "auto" }
    Object.assign(style, pe)
    return style
  }

  const fullscreenButtonRef = useRef<HTMLButtonElement>(null)

  const onEnterFullscreen = () => setShowModal(true)

  const onExitFullscreen = () => {
    setShowModal(false)
    requestAnimationFrame(() => {
      fullscreenButtonRef.current?.focus()
    })
  }

  const handleZoomInOut =
    (decrease = false) =>
    () => {
      if (!transformRef.current || isStrikethrough) return

      const transformState = getTransformState(transformRef.current)
      if (!transformState) return

      const newScale = getNextScale(transformState.scale ?? zoom, decrease)
      const centeredZoomPosition = getCenteredPosition(
        transformRef.current,
        newScale
      )
      const { positionX, positionY } = getBoundedPosition(
        centeredZoomPosition,
        getCurrentBounds(transformRef.current, newScale)
      )

      setZoom(newScale)
      transformRef.current.setTransform(positionX, positionY, newScale, 0)
    }

  const handleResetZoom = () => {
    if (!transformRef.current || isStrikethrough) return

    const { positionX, positionY } = getBoundedPosition(
      { positionX: 0, positionY: 0 },
      getCurrentBounds(transformRef.current, MIN_SCALE)
    )

    setZoom(MIN_SCALE)
    transformRef.current.setTransform(positionX, positionY, MIN_SCALE, 0)
  }

  const handlePan = (direction: PanDirection) => () => {
    if (!transformRef.current || zoom <= MIN_SCALE) return

    const transformState = getTransformState(transformRef.current)
    if (!transformState || (transformState.scale ?? MIN_SCALE) <= MIN_SCALE) {
      return
    }

    const nextPosition = getPannedPosition(
      transformState.positionX ?? 0,
      transformState.positionY ?? 0,
      direction
    )
    const { positionX, positionY } = getBoundedPosition(
      nextPosition,
      getCurrentBounds(transformRef.current, transformState.scale ?? zoom)
    )

    transformRef.current.setTransform(
      positionX,
      positionY,
      transformState.scale ?? zoom,
      0
    )
  }

  const handleViewerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.currentTarget !== event.target) return
    if (disableInlineZoom || zoom <= MIN_SCALE) return

    const direction = getPanDirectionFromKey(event.key)
    if (!direction) return

    handlePan(direction)()
    event.preventDefault()
    event.stopPropagation()
  }

  const handleImageMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (disableInlineZoom || isStrikethrough) return
    if (event.button !== 0) return
    if (zoom !== MIN_SCALE) return

    event.preventDefault()
    event.stopPropagation()
    wrapperRef.current?.focus()
    handleZoomInOut(false)()
  }

  return (
    <Box
      ref={wrapperRef}
      component={"div"}
      tabIndex={tabIndex}
      className={classes.centeredContainer}
      onMouseOver={() => setShowButtons(true)}
      onMouseLeave={() => setShowButtons(false)}
      onFocus={() => setShowButtons(true)}
      onKeyDown={handleViewerKeyDown}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setShowButtons(false)
        }
      }}
      onClick={event => stopPropagation && event.stopPropagation()}
      role="region"
      aria-label="Zoomable image viewer"
    >
      <TransformWrapper
        initialScale={MIN_SCALE}
        ref={transformRef}
        maxScale={MAX_SCALE}
        minScale={MIN_SCALE}
        doubleClick={{
          disabled: true
        }}
        wheel={{ disabled: true }}
        limitToBounds={true}
        centerOnInit={true}
        onTransformed={(_, state) => setZoom(clampScale(state.scale))}
      >
        {({ state }) => {
          const disableZoomOut = !(zoom > MIN_SCALE && state.scale > MIN_SCALE)
          const disableZoomIn = state.scale >= MAX_SCALE
          const canPan = !disableInlineZoom && zoom > MIN_SCALE

          return (
            <>
              {!imageLoaded || blobLoading ? (
                <Skeleton
                  className={classes.skeleton}
                  variant="rectangular"
                  animation="wave"
                />
              ) : (
                <Box
                  className={cx(classes.zoomButtons, showButtons && "visible")}
                  role="group"
                  aria-label="Image zoom controls"
                >
                  {!disableInlineZoom && (
                    <>
                      <IconButton
                        aria-disabled={disableZoomOut || isStrikethrough}
                        className={classes.iconRoot}
                        onClick={
                          disableZoomOut || isStrikethrough
                            ? undefined
                            : defaultPrevented(handleZoomInOut(true))
                        }
                        size="large"
                        aria-label="Zoom out"
                        data-automation-id={formatDataAutomationId(
                          "image-zoom-out"
                        )}
                      >
                        <i
                          className={cx("fa fa-search-minus", classes.icon)}
                          aria-hidden="true"
                        ></i>
                      </IconButton>
                      {zoomPercentage(
                        zoom,
                        handleResetZoom,
                        ZOOM_PERCENTAGE_ID,
                        isStrikethrough
                      )}
                      <IconButton
                        className={classes.iconRoot}
                        onClick={
                          disableZoomIn || isStrikethrough
                            ? undefined
                            : defaultPrevented(handleZoomInOut(false))
                        }
                        size="large"
                        aria-disabled={disableZoomIn || isStrikethrough}
                        aria-label="Zoom in"
                        data-automation-id={formatDataAutomationId(
                          "image-zoom-in"
                        )}
                      >
                        <i
                          className={cx("fa fa-search-plus", classes.icon)}
                          aria-hidden="true"
                        ></i>
                      </IconButton>
                      {canPan && (
                        <Box
                          className={classes.panButtons}
                          role="group"
                          aria-label="Image pan controls"
                        >
                          <IconButton
                            className={classes.iconRoot}
                            onClick={defaultPrevented(handlePan("up"))}
                            size="large"
                            aria-label="Pan image up"
                            data-automation-id={formatDataAutomationId(
                              "image-pan-up"
                            )}
                          >
                            <i
                              className={cx("fa fa-arrow-up", classes.icon)}
                              aria-hidden="true"
                            ></i>
                          </IconButton>
                          <IconButton
                            className={classes.iconRoot}
                            onClick={defaultPrevented(handlePan("down"))}
                            size="large"
                            aria-label="Pan image down"
                            data-automation-id={formatDataAutomationId(
                              "image-pan-down"
                            )}
                          >
                            <i
                              className={cx("fa fa-arrow-down", classes.icon)}
                              aria-hidden="true"
                            ></i>
                          </IconButton>
                          <IconButton
                            className={classes.iconRoot}
                            onClick={defaultPrevented(handlePan("left"))}
                            size="large"
                            aria-label="Pan image left"
                            data-automation-id={formatDataAutomationId(
                              "image-pan-left"
                            )}
                          >
                            <i
                              className={cx("fa fa-arrow-left", classes.icon)}
                              aria-hidden="true"
                            ></i>
                          </IconButton>
                          <IconButton
                            className={classes.iconRoot}
                            onClick={defaultPrevented(handlePan("right"))}
                            size="large"
                            aria-label="Pan image right"
                            data-automation-id={formatDataAutomationId(
                              "image-pan-right"
                            )}
                          >
                            <i
                              className={cx("fa fa-arrow-right", classes.icon)}
                              aria-hidden="true"
                            ></i>
                          </IconButton>
                        </Box>
                      )}
                    </>
                  )}

                  <IconButton
                    ref={fullscreenButtonRef}
                    className={classes.iconRoot}
                    onClick={defaultPrevented(onEnterFullscreen)}
                    size="large"
                    aria-label="Open image in fullscreen"
                    data-automation-id={formatDataAutomationId(
                      "image-fullscreen"
                    )}
                  >
                    <i
                      className={cx("fa fa-arrows-alt", classes.icon)}
                      aria-hidden="true"
                    ></i>
                  </IconButton>
                </Box>
              )}

              <Box display="flex" onMouseDown={handleImageMouseDown}>
                <TransformComponent
                  contentStyle={{ width: "100%" }}
                  wrapperStyle={{ width: "100%" }}
                >
                  <img
                    onLoad={event => {
                      setImageLoaded(true)
                      onImageLoaded?.(event)
                    }}
                    ref={setImageRef}
                    src={src}
                    alt={imageProps?.alt ?? ""}
                    // a11y: answer-choice radio's aria-labelledby target (choice-content-{idx});
                    // keep this id on the inline img only — never the Modal img (a duplicate id breaks IDREF)
                    id={imageProps?.id}
                    data-idx={imageProps?.["data-idx"]}
                    style={
                      styles
                        ? getStyleObjectFromString(styles?.toString())
                        : { pointerEvents: "auto" }
                    }
                    className={cx(
                      className,
                      classes.image,
                      !imageLoaded && classes.imageHidden,
                      !disableInlineZoom && "zoomable",
                      state.scale !== 1 && "grabable"
                    )}
                  />
                  {imageProps?.["data-description"] && (
                    <figcaption style={{ display: "none" }}>
                      {imageProps?.["data-description"]}
                    </figcaption>
                  )}
                </TransformComponent>
              </Box>
            </>
          )
        }}
      </TransformWrapper>
      {showModal && (
        <Modal
          open={showModal}
          src={src}
          onClose={onExitFullscreen}
          className={modalImageClassname || className}
          imageProps={imageProps}
        />
      )}
    </Box>
  )
})

const zoomPercentage = (
  state: number,
  onResetZoom: () => void,
  id: string = ZOOM_PERCENTAGE_ID,
  isStrikethrough = false
) => {
  const zoom = Math.floor(state * 100)
  return (
    <>
      <Chip
        onClick={onResetZoom}
        disabled={isStrikethrough}
        label={`${zoom}%`}
        aria-label={`Zoom is at ${zoom}%${
          zoom !== 100 ? ". Reset to 100%" : ""
        }`}
        data-automation-id={formatDataAutomationId("image-zoom-display")}
      />
      <Box
        id={id}
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        style={visuallyHidden}
      >
        {`${zoom}%`}
      </Box>
    </>
  )
}

type ModalProps = {
  open: boolean
  onClose: () => void
  src?: string
  className?: string
  imageProps?: React.ImgHTMLAttributes<HTMLImageElement> & {
    "data-description"?: string
  }
}

const Modal: React.FC<ModalProps> = ({
  open,
  src,
  onClose,
  className,
  imageProps
}) => {
  const { classes, cx } = useStyles()

  const [modalZoom, setModalZoom] = useState(MIN_SCALE)
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const handleZoomInOut =
    (decrease = false) =>
    () => {
      if (!transformRef.current) return

      const transformState = getTransformState(transformRef.current)
      if (!transformState) return

      const newScale = getNextScale(transformState.scale ?? modalZoom, decrease)
      const centeredZoomPosition = getCenteredPosition(
        transformRef.current,
        newScale
      )
      const { positionX, positionY } = getBoundedPosition(
        centeredZoomPosition,
        getCurrentBounds(transformRef.current, newScale)
      )

      setModalZoom(newScale)
      transformRef.current.setTransform(positionX, positionY, newScale, 0)
    }

  const handleResetZoom = () => {
    if (!transformRef.current) return

    const { positionX, positionY } = getBoundedPosition(
      { positionX: 0, positionY: 0 },
      getCurrentBounds(transformRef.current, MIN_SCALE)
    )

    setModalZoom(MIN_SCALE)
    transformRef.current.setTransform(positionX, positionY, MIN_SCALE, 0)
  }

  const handlePan = (direction: PanDirection) => () => {
    if (!transformRef.current || modalZoom <= MIN_SCALE) return

    const transformState = getTransformState(transformRef.current)
    if (!transformState || (transformState.scale ?? MIN_SCALE) <= MIN_SCALE) {
      return
    }

    const nextPosition = getPannedPosition(
      transformState.positionX ?? 0,
      transformState.positionY ?? 0,
      direction
    )
    const { positionX, positionY } = getBoundedPosition(
      nextPosition,
      getCurrentBounds(transformRef.current, transformState.scale ?? modalZoom)
    )

    transformRef.current.setTransform(
      positionX,
      positionY,
      transformState.scale ?? modalZoom,
      0
    )
  }

  const handleViewerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.currentTarget !== event.target) return
    if (modalZoom <= MIN_SCALE) return

    const direction = getPanDirectionFromKey(event.key)
    if (!direction) return

    handlePan(direction)()
    event.preventDefault()
    event.stopPropagation()
  }

  const handleImageMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (event.button !== 0) return
    if (modalZoom !== MIN_SCALE) return

    event.preventDefault()
    event.stopPropagation()
    contentRef.current?.focus()
    handleZoomInOut(false)()
  }

  // keeping it mounted
  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted={true}
      disableRestoreFocus
      classes={{ paper: classes.modalPaper }}
      maxWidth={"md" as Breakpoint}
      aria-label="Fullscreen image viewer"
      role="dialog"
    >
      <TransformWrapper
        initialScale={MIN_SCALE}
        ref={transformRef}
        maxScale={MAX_SCALE}
        minScale={MIN_SCALE}
        doubleClick={{
          disabled: true
        }}
        wheel={{ disabled: true }}
        limitToBounds={true}
        centerOnInit={true}
        onTransformed={(_, state) => setModalZoom(clampScale(state.scale))}
      >
        {({ state }) => {
          const disableZoomOut = !(
            modalZoom > MIN_SCALE && state.scale > MIN_SCALE
          )
          const disableZoomIn = state.scale >= MAX_SCALE
          const canPan = modalZoom > MIN_SCALE

          return (
            <>
              <Box className={classes.modalButtons}>
                <IconButton
                  className={classes.iconRoot}
                  onClick={onClose}
                  size="large"
                  aria-label="Close fullscreen image"
                  data-automation-id={formatDataAutomationId(
                    "image-fullscreen-close"
                  )}
                >
                  <FaIcon solid name="times" aria-hidden="true" />
                </IconButton>

                <Box role="group" aria-label="Image zoom controls">
                  <IconButton
                    aria-disabled={disableZoomOut}
                    className={classes.iconRoot}
                    onClick={disableZoomOut ? undefined : handleZoomInOut(true)}
                    size="large"
                    aria-label="Zoom out"
                    data-automation-id={formatDataAutomationId(
                      "image-zoom-out-modal"
                    )}
                  >
                    <i
                      className={cx("fa fa-search-minus", classes.icon)}
                      aria-hidden="true"
                    ></i>
                  </IconButton>
                  {zoomPercentage(
                    modalZoom,
                    handleResetZoom,
                    ZOOM_PERCENTAGE_ID_MODAL
                  )}
                  <IconButton
                    aria-disabled={disableZoomIn}
                    className={classes.iconRoot}
                    onClick={disableZoomIn ? undefined : handleZoomInOut(false)}
                    size="large"
                    aria-label="Zoom in"
                    data-automation-id={formatDataAutomationId(
                      "image-zoom-in-modal"
                    )}
                  >
                    <i
                      className={cx("fa fa-search-plus", classes.icon)}
                      aria-hidden="true"
                    ></i>
                  </IconButton>
                  {canPan && (
                    <Box
                      className={classes.panButtons}
                      role="group"
                      aria-label="Image pan controls"
                    >
                      <IconButton
                        className={classes.iconRoot}
                        onClick={defaultPrevented(handlePan("up"))}
                        size="large"
                        aria-label="Pan image up"
                        data-automation-id={formatDataAutomationId(
                          "image-pan-up-modal"
                        )}
                      >
                        <i
                          className={cx("fa fa-arrow-up", classes.icon)}
                          aria-hidden="true"
                        ></i>
                      </IconButton>
                      <IconButton
                        className={classes.iconRoot}
                        onClick={defaultPrevented(handlePan("down"))}
                        size="large"
                        aria-label="Pan image down"
                        data-automation-id={formatDataAutomationId(
                          "image-pan-down-modal"
                        )}
                      >
                        <i
                          className={cx("fa fa-arrow-down", classes.icon)}
                          aria-hidden="true"
                        ></i>
                      </IconButton>
                      <IconButton
                        className={classes.iconRoot}
                        onClick={defaultPrevented(handlePan("left"))}
                        size="large"
                        aria-label="Pan image left"
                        data-automation-id={formatDataAutomationId(
                          "image-pan-left-modal"
                        )}
                      >
                        <i
                          className={cx("fa fa-arrow-left", classes.icon)}
                          aria-hidden="true"
                        ></i>
                      </IconButton>
                      <IconButton
                        className={classes.iconRoot}
                        onClick={defaultPrevented(handlePan("right"))}
                        size="large"
                        aria-label="Pan image right"
                        data-automation-id={formatDataAutomationId(
                          "image-pan-right-modal"
                        )}
                      >
                        <i
                          className={cx("fa fa-arrow-right", classes.icon)}
                          aria-hidden="true"
                        ></i>
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box
                ref={contentRef}
                onKeyDown={handleViewerKeyDown}
                onMouseDown={handleImageMouseDown}
                className={classes.modalContentWrapper}
                role="region"
                aria-label="Fullscreen zoomable image viewer"
                tabIndex={0}
              >
                <TransformComponent
                  contentStyle={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center"
                  }}
                  wrapperStyle={{ width: "100%", height: "100%" }}
                >
                  <img
                    src={src}
                    // a11y: intentionally no id here — the inline img owns imageProps.id
                    // (choice-content); a duplicate id would break the radio's aria-labelledby IDREF
                    alt={imageProps?.alt ?? ""}
                    style={{
                      pointerEvents: "auto",
                      height: "100%",
                      objectFit: "contain"
                    }}
                    className={cx(
                      className,
                      classes.image,
                      state.scale !== 1 && "grabable"
                    )}
                  />
                  {imageProps?.["data-description"] && (
                    <figcaption style={{ display: "none" }}>
                      {imageProps?.["data-description"]}
                    </figcaption>
                  )}
                </TransformComponent>
              </Box>
            </>
          )
        }}
      </TransformWrapper>
    </Dialog>
  )
}

const useStyles = makeStyles()(({ breakpoints }) => ({
  iconRoot: {
    height: "35px",
    width: "35px",
    color: blueGrey[500],
    "& svg": {},
    // a11y: keyboard focus ring only (:focus-visible); on mouse :focus suppress
    // the ring so a click doesn't leave a stuck ring (SC 2.4.7)
    "&:focus-visible": {
      ...focusRing.ring
    },
    "&:focus:not(:focus-visible)": {
      outline: "none",
      boxShadow: "none"
    },
    "&[aria-disabled='true']": {
      color: blueGrey[200]
    }
  },
  zoomButtons: {
    display: "flex",
    // a11y: wrap so controls reflow to a second line instead of clipping off
    // the left at 400% zoom / 320px width (SC 1.4.10)
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    rowGap: 2,
    width: "100%",
    marginBottom: 2,
    visibility: "hidden",

    "&.visible": {
      visibility: "visible"
    },
    [breakpoints.down("md" as Breakpoint)]: {
      visibility: "visible !important" as "visible"
    },

    "& > *:not(:last-child)": {
      marginRight: 2
    },
    // a11y: the reset chip gets its own focus ring instead of the global one (SC 2.4.7)
    "& .MuiChip-root:focus-visible": {
      ...focusRing.ring
    },
    "& .MuiChip-root:focus:not(:focus-visible)": {
      outline: "none",
      boxShadow: "none"
    }
  },
  panButtons: {
    display: "inline-flex",
    // a11y: wrap the 4 arrows so the pan group (the one unbreakable ~140px unit,
    // shown only when zoomed in) reflows instead of overflowing/clipping in a
    // narrow cell at high zoom (SC 1.4.10)
    flexWrap: "wrap",
    alignItems: "center"
  },
  modalPaper: {
    padding: "0.5rem",
    width: "100%",
    maxWidth: "70vw",
    height: "70vh",

    [breakpoints.down("sm" as Breakpoint)]: {
      margin: "0px",
      boxSizing: "border-box",
      width: "100%",
      height: `100%`,
      maxHeight: "none",
      maxWidth: "none"
    }
  },
  modalContentWrapper: {
    height: "calc(70vh - 56px)",
    "&:focus-visible": {
      outline: `2px solid ${blueGrey[500]}`,
      outlineOffset: 2
    },
    [breakpoints.down("sm" as Breakpoint)]: {
      height: "100%"
    }
  },
  modalButtons: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    marginBottom: 4,
    // a11y: the reset chip gets its own focus ring instead of the global one (SC 2.4.7)
    "& .MuiChip-root:focus-visible": {
      ...focusRing.ring
    },
    "& .MuiChip-root:focus:not(:focus-visible)": {
      outline: "none",
      boxShadow: "none"
    }
  },
  icon: {
    fontSize: "1rem",
    lineHeight: 0
  },
  image: {
    maxHeight: "calc(100vh - 120px)",
    "&.zoomable": {
      "&:hover": {
        cursor: "zoom-in"
      }
    },
    "&.grabable": {
      cursor: "grab",
      "&:active": {
        cursor: "grabbing"
      }
    }
  },
  imageHidden: {
    height: 0,
    width: 0
  },
  skeleton: {
    marginBottom: "1.25rem",
    width: "100%",
    height: 200
  },
  centeredContainer: {
    margin: "0 auto",
    pointerEvents: "auto",
    "&:focus-visible": {
      outline: `2px solid ${blueGrey[500]}`,
      outlineOffset: 2
    }
  }
}))

export default ImageMagnifier
